/**
 * Create Company/Contact Page
 *
 * Full-page form for creating a new company or contact.
 */

import { Building2 } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { CreateCompanyForm } from '../components/CreateCompanyForm';
import { useCreateCompany } from '../hooks';
import type { ContactType } from '../types';

export default function CreateCompanyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contactTypeParam = searchParams.get('type') as ContactType | null;
  const contactType: ContactType = contactTypeParam || 'organization';

  const createCompany = useCreateCompany();
  const { setPageHeader, clearPageHeader, setActions } = usePageHeader();

  // Set page header with breadcrumbs and actions
  useEffect(() => {
    setPageHeader({
      title: contactType === 'individual' ? 'Add Individual Contact' : 'Add Organization',
      breadcrumbs: [
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Contacts', href: '/contacts' },
        { label: contactType === 'individual' ? 'Add Individual' : 'Add Organization' },
      ],
    });

    // Add cancel button to header actions
    setActions(
      <Button variant="outline" onClick={() => navigate('/contacts')}>
        Cancel
      </Button>,
    );

    return () => clearPageHeader();
  }, [setPageHeader, clearPageHeader, setActions, navigate, contactType]);

  const handleCancel = () => {
    navigate('/contacts');
  };

  const handleSuccess = (company: any) => {
    navigate(`/contacts/${company.id}`);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Description */}
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
          <Building2 className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-foreground">
            {contactType === 'individual' ? 'Individual Information' : 'Organization Information'}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {contactType === 'individual'
              ? 'Fill in the details below to add an individual contact.'
              : 'Fill in the details below. Company name and category are required for organizations.'}
          </p>
        </div>
      </div>

      {/* Form Card */}
      <Card>
        <CardContent className="pt-6">
          <CreateCompanyForm
            contactType={contactType}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            isPending={createCompany.isPending}
          />
        </CardContent>
      </Card>
    </div>
  );
}

CreateCompanyPage.displayName = 'CreateCompanyPage';
